import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { deploymentService, type Deployment } from '@/services/deployment/deployment.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Plus, Globe, Server, Edit } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

export default function DeploymentsListPage() {
    const navigate = useNavigate();
    const { data: deployments, isLoading, error } = useQuery({
        queryKey: ['deployments', 'all'],
        queryFn: () => deploymentService.getAll(),
    });

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'DEPLOYED':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'PENDING_DEPLOYMENT':
                return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
            case 'PROVISIONING':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'PROVISION_FAILED':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            case 'SUSPENDED':
                return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
            case 'TERMINATED':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const STATUS_LABELS: Record<string, string> = {
        PENDING_DEPLOYMENT: 'Pending deployment',
        PROVISIONING: 'Provisioning',
        PROVISION_FAILED: 'Provision failed',
    };

    const statusLabel = (status: string) =>
        STATUS_LABELS[status] ?? status.charAt(0) + status.slice(1).toLowerCase();

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <Skeleton className="h-8 w-40" />
                    <Skeleton className="h-10 w-36" />
                </div>
                <div className="grid gap-4">
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-40 w-full" />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <h1 className="text-2xl font-semibold">Deployments</h1>
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        Failed to load deployments. Please try again later.
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold">Deployments</h1>
                <Button onClick={() => navigate('/admin/deployments/create')}>
                    <Plus className="mr-2 h-4 w-4" />
                    New Deployment
                </Button>
            </div>

            {!deployments || deployments.length === 0 ? (
                <Card>
                    <CardContent className="p-6">
                        <p className="text-center text-slate-500 dark:text-slate-400">
                            No deployments found. Create a new deployment to get started.
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4">
                    {deployments.map((deployment: Deployment) => (
                        <Card key={deployment.id} className="hover:shadow-md transition-shadow">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="text-lg flex items-center gap-2">
                                            <Server className="h-5 w-5" />
                                            {deployment.subscription?.company?.name || 'Unknown Company'}
                                        </CardTitle>
                                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                            {deployment.subscription?.user?.email}
                                        </p>
                                    </div>
                                    <Badge className={getStatusColor(deployment.status)}>
                                        {statusLabel(deployment.status)}
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2 text-sm">
                                        <div className="flex items-center gap-2">
                                            <Globe className="h-4 w-4 text-slate-400" />
                                            <span className="text-slate-500 dark:text-slate-400">Frontend:</span>
                                            <span className="font-mono">{deployment.frontendDomain}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Globe className="h-4 w-4 text-slate-400" />
                                            <span className="text-slate-500 dark:text-slate-400">Backend:</span>
                                            <span className="font-mono">{deployment.backendDomain}</span>
                                        </div>
                                        {deployment.filesDomain && (
                                            <div className="flex items-center gap-2">
                                                <Globe className="h-4 w-4 text-slate-400" />
                                                <span className="text-slate-500 dark:text-slate-400">Files:</span>
                                                <span className="font-mono">{deployment.filesDomain}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="space-y-2 text-sm">
                                        <p>
                                            <span className="text-slate-500 dark:text-slate-400">IP: </span>
                                            <span className="font-mono">{deployment.ipAddress}</span>
                                        </p>
                                        <p>
                                            <span className="text-slate-500 dark:text-slate-400">Plan: </span>
                                            {deployment.subscription?.plan?.displayName}
                                        </p>
                                        <p>
                                            <span className="text-slate-500 dark:text-slate-400">Created: </span>
                                            {format(new Date(deployment.createdAt), 'PP')}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex justify-end mt-4">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => navigate(`/admin/deployments/${deployment.id}`)}
                                    >
                                        <Edit className="mr-2 h-4 w-4" />
                                        Edit
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
